import { useEffect, useMemo, useState, Fragment } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ShoppingBag, Plus, Minus, CheckCircle2, Check } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function ProductPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { add, setOpen } = useCart();
  const [all, setAll] = useState([]);
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [justAdded, setJustAdded] = useState(false);
  const [kitPicks, setKitPicks] = useState({});
  const [kitAdded, setKitAdded] = useState(false);

  useEffect(() => {
    setLoading(true);
    // Fetch full catalog for the sticky info + fallback "you might also like"
    // and the bundled recommendations for this product.
    Promise.all([
      api.get("/products").then((r) => r.data.products),
      api.get(`/products/${slug}/recommendations`).then((r) => r.data.recommendations).catch(() => []),
    ])
      .then(([products, recommendations]) => {
        setAll(products);
        setRecs(recommendations);
        // Pre-select the first two recommendations for the bundle.
        setKitPicks(
          recommendations.reduce((acc, r, i) => ({ ...acc, [r.slug]: i < 2 }), {})
        );
      })
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  const product = useMemo(() => all.find((p) => p.slug === slug), [all, slug]);
  const related = useMemo(
    () => {
      if (!product) return [];
      const recSlugs = new Set(recs.map((r) => r.slug));
      return all
        .filter(
          (p) =>
            p.category === product.category &&
            p.slug !== product.slug &&
            !recSlugs.has(p.slug)
        )
        .slice(0, 4);
    },
    [all, product, recs]
  );

  // Product can carry an optional `images` array; fall back to the single image.
  const gallery = useMemo(() => {
    if (!product) return [];
    if (product.images && product.images.length > 0) return product.images;
    return [product.image];
  }, [product]);

  const hasSale = product?.sale_price != null && product.sale_price < product.price;
  const price = hasSale ? product.sale_price : product?.price;

  const handleAdd = () => {
    if (!product) return;
    add(product, qty);
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1600);
  };

  if (loading) {
    return (
      <div className="bg-[#050505] min-h-screen">
        <Nav />
        <div className="pt-40 text-center text-neutral-500 font-mono text-xs tracking-widest uppercase">
          Loading product…
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="bg-[#050505] min-h-screen">
        <Nav />
        <main className="pt-40 pb-24 max-w-[900px] mx-auto px-6 md:px-12 text-center">
          <p className="eyebrow mb-4">404</p>
          <h1 className="font-serif text-5xl md:text-7xl text-white leading-[0.95]">
            Product <span className="italic text-brand-red">not found.</span>
          </h1>
          <Link to="/shop" className="btn-primary mt-8 inline-flex">
            <ArrowLeft size={14} /> Back to Shop
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="grain bg-[#050505] min-h-screen">
      <Nav />
      <main className="pt-28 md:pt-36 pb-24">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          {/* Breadcrumb */}
          <div className="mb-8 flex items-center gap-3 font-mono text-[10px] tracking-widest uppercase text-neutral-500">
            <Link to="/shop" className="hover:text-white">Shop</Link>
            <span>/</span>
            <span>{product.category}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Gallery */}
            <div className="lg:col-span-7">
              <motion.div
                key={activeImg}
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="aspect-square bg-[#0a0a0a] overflow-hidden border border-white/5"
              >
                <img
                  src={gallery[activeImg]}
                  alt={product.name}
                  data-testid="product-main-image"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              {gallery.length > 1 && (
                <div className="mt-4 grid grid-cols-4 md:grid-cols-5 gap-3">
                  {gallery.map((src, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      data-testid={`product-thumb-${i}`}
                      className={`aspect-square overflow-hidden border transition-all ${
                        activeImg === i ? "border-white" : "border-white/10 hover:border-white/40"
                      }`}
                    >
                      <img
                        src={src}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <aside className="lg:col-span-5 lg:sticky lg:top-32 self-start">
              {product.badge && (
                <p className="mb-4 inline-block px-3 py-1 bg-white/10 backdrop-blur font-mono text-[9px] tracking-[0.24em] uppercase text-white">
                  {product.badge}
                </p>
              )}
              <p className="eyebrow mb-3">{product.category}</p>
              <h1
                data-testid="product-name"
                className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-[0.95]"
              >
                {product.name}
              </h1>

              <div className="mt-8 flex items-baseline gap-4">
                <span
                  data-testid="product-price"
                  className="font-serif text-5xl text-white"
                >
                  ${price.toFixed(2)}
                </span>
                {hasSale && (
                  <span className="font-mono text-sm text-neutral-500 line-through">
                    ${product.price.toFixed(2)}
                  </span>
                )}
                <span className="font-mono text-[10px] tracking-widest uppercase text-neutral-500">
                  AUD
                </span>
              </div>

              <p className="mt-8 text-neutral-400 leading-relaxed whitespace-pre-line">
                {product.description ||
                  "Professional-grade detailing supply from SEVEN7H — the exact tools we use in the studio. Ships from Paralowie, SA. Fast dispatch. Free shipping on orders over $200."}
              </p>

              {/* Quantity + Add */}
              <div className="mt-10 flex flex-col sm:flex-row gap-4 items-stretch">
                <div className="flex items-center gap-2 border border-white/15 self-start">
                  <button
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    data-testid="product-qty-dec"
                    className="w-11 h-11 text-white hover:bg-white hover:text-black transition-colors flex items-center justify-center"
                  >
                    <Minus size={14} />
                  </button>
                  <span
                    data-testid="product-qty"
                    className="w-10 text-center text-white text-lg font-serif"
                  >
                    {qty}
                  </span>
                  <button
                    onClick={() => setQty((q) => Math.min(99, q + 1))}
                    data-testid="product-qty-inc"
                    className="w-11 h-11 text-white hover:bg-white hover:text-black transition-colors flex items-center justify-center"
                  >
                    <Plus size={14} />
                  </button>
                </div>
                <button
                  onClick={handleAdd}
                  data-testid="product-add-to-cart"
                  className="btn-primary flex-1 justify-center"
                >
                  {justAdded ? (
                    <>
                      <CheckCircle2 size={14} /> Added to Cart
                    </>
                  ) : (
                    <>
                      <ShoppingBag size={14} /> Add to Cart · ${(price * qty).toFixed(2)}
                    </>
                  )}
                </button>
              </div>

              <button
                onClick={() => {
                  add(product, qty);
                  nav("/checkout");
                }}
                data-testid="product-buy-now"
                className="mt-4 btn-ghost w-full justify-center"
              >
                Buy Now <ArrowRight size={14} />
              </button>

              {/* Details block */}
              <ul className="mt-10 border-t border-white/10 divide-y divide-white/10 text-sm">
                <li className="py-3 flex justify-between">
                  <span className="text-neutral-500">Category</span>
                  <span className="text-white">{product.category}</span>
                </li>
                <li className="py-3 flex justify-between">
                  <span className="text-neutral-500">Ships From</span>
                  <span className="text-white">Paralowie, SA</span>
                </li>
                <li className="py-3 flex justify-between">
                  <span className="text-neutral-500">Delivery</span>
                  <span className="text-white">Flat $15 · Free over $200</span>
                </li>
              </ul>
            </aside>
          </div>

          {/* Complete the Kit — bundle upsell */}
          {recs.length > 0 && (
            <BundleUpsell
              base={product}
              basePrice={price}
              recs={recs}
              picks={kitPicks}
              setPicks={setKitPicks}
              onAddKit={() => {
                add(product, 1);
                recs.forEach((r) => {
                  if (kitPicks[r.slug]) add(r, 1);
                });
                setKitAdded(true);
                setTimeout(() => setKitAdded(false), 2000);
              }}
              kitAdded={kitAdded}
            />
          )}

          {/* You might also like — same-category browse fallback */}
          {related.length > 0 && (
            <section className="mt-24 md:mt-32 border-t border-white/10 pt-16">
              <p className="eyebrow mb-8">You might also like</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/shop/${r.slug}`}
                    className="group block"
                    data-testid={`related-product-${r.slug}`}
                  >
                    <div className="aspect-square overflow-hidden bg-[#0a0a0a] border border-white/5 group-hover:border-white/25 transition-colors">
                      <img
                        src={r.image}
                        alt={r.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                      />
                    </div>
                    <p className="mt-3 font-mono text-[10px] tracking-widest uppercase text-neutral-500">
                      {r.category}
                    </p>
                    <p className="font-serif text-lg text-white leading-tight mt-1">
                      {r.name}
                    </p>
                    <p className="mt-2 font-serif text-xl text-white">
                      ${(r.sale_price ?? r.price).toFixed(2)}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}


function BundleUpsell({ base, basePrice, recs, picks, setPicks, onAddKit, kitAdded }) {
  const selectedRecs = recs.filter((r) => picks[r.slug]);
  const addOnTotal = selectedRecs.reduce(
    (s, r) => s + (r.sale_price ?? r.price),
    0
  );
  const listPrice =
    (base.price || 0) +
    selectedRecs.reduce((s, r) => s + (r.price || 0), 0);
  const kitTotal = basePrice + addOnTotal;
  const savings = Math.max(0, listPrice - kitTotal);
  const count = 1 + selectedRecs.length;

  const toggle = (slug) =>
    setPicks((p) => ({ ...p, [slug]: !p[slug] }));

  return (
    <section
      className="mt-24 md:mt-32 border-t border-white/10 pt-16"
      data-testid="bundle-upsell"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end mb-10">
        <div className="md:col-span-8">
          <p className="eyebrow mb-3">Complete the Kit</p>
          <h2 className="font-serif text-4xl md:text-5xl text-white leading-[0.95]">
            Ship it together.{" "}
            <span className="italic text-brand-red">Detail smarter.</span>
          </h2>
          <p className="mt-4 text-neutral-400 max-w-xl">
            The exact companion products SEVEN7H uses alongside this SKU in the
            studio. Uncheck what you don't want — everything ships in one
            parcel, free over $200.
          </p>
        </div>
        <div className="md:col-span-4 md:text-right">
          <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500 mb-2">
            Kit total · {count} item{count > 1 ? "s" : ""}
          </p>
          <p
            data-testid="bundle-kit-total"
            className="font-serif text-5xl md:text-6xl text-white"
          >
            ${kitTotal.toFixed(2)}
          </p>
          {savings > 0 && (
            <p className="mt-1 font-mono text-[10px] tracking-widest uppercase text-brand-red">
              You save ${savings.toFixed(2)}
            </p>
          )}
        </div>
      </div>

      {/* Bundle line — base + plus signs + each rec */}
      <div className="flex flex-wrap items-center gap-4 md:gap-6">
        <BundleTile item={{ ...base, price: basePrice }} locked />
        {recs.map((r) => (
          <Fragment key={r.slug}>
            <span className="font-serif text-3xl text-neutral-600 select-none">
              +
            </span>
            <BundleTile
              item={r}
              checked={!!picks[r.slug]}
              onToggle={() => toggle(r.slug)}
            />
          </Fragment>
        ))}
      </div>

      <div className="mt-10 flex flex-col sm:flex-row gap-4 items-stretch">
        <button
          onClick={onAddKit}
          disabled={count < 2}
          data-testid="bundle-add-to-cart"
          className="btn-primary flex-1 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {kitAdded ? (
            <>
              <CheckCircle2 size={14} /> Kit Added to Cart
            </>
          ) : (
            <>
              <ShoppingBag size={14} /> Add {count} Items to Cart · $
              {kitTotal.toFixed(2)}
            </>
          )}
        </button>
      </div>
    </section>
  );
}

function BundleTile({ item, locked = false, checked = false, onToggle }) {
  const p = item.sale_price ?? item.price;
  const active = locked || checked;
  return (
    <button
      type="button"
      onClick={locked ? undefined : onToggle}
      data-testid={locked ? "bundle-base-tile" : `bundle-tile-${item.slug}`}
      className={`group relative flex-1 min-w-[160px] max-w-[220px] text-left transition-all ${
        locked ? "cursor-default" : "cursor-pointer"
      }`}
    >
      <div
        className={`relative aspect-square overflow-hidden border transition-all ${
          active
            ? "border-white"
            : "border-white/10 hover:border-white/40 opacity-50 hover:opacity-80"
        } bg-[#0a0a0a]`}
      >
        <img
          src={item.image}
          alt={item.name}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        {!locked && (
          <div
            className={`absolute top-2 right-2 w-6 h-6 flex items-center justify-center border transition-colors ${
              checked
                ? "bg-brand-red border-brand-red text-white"
                : "bg-black/60 border-white/40 text-transparent"
            }`}
          >
            <Check size={12} strokeWidth={3} />
          </div>
        )}
        {locked && (
          <div className="absolute top-2 left-2 px-2 py-0.5 bg-white text-black font-mono text-[9px] tracking-[0.2em] uppercase">
            This item
          </div>
        )}
      </div>
      <p className="mt-2 font-serif text-sm text-white leading-tight line-clamp-2">
        {item.name}
      </p>
      <p className="font-mono text-xs text-neutral-500 mt-1">
        ${p.toFixed(2)}
      </p>
    </button>
  );
}
